/**
 * Component to display notifications related to the monthly
 * included Returns-Volume of a user once the user approaches
 * their limit.
 *
 * (1) > 80% of volume
 * (2) > 100% of volume (in overage, extra returns being charged)
 * (3) using their usage-charge balance on the Shopify account billed
 * per additional Return, we display a countdown of remaining Returns
 * (4) usage-charge limit on account reached, no further Returns possible
 * (5) billing cap is below the current plan maximum — merchant needs to approve an increase
 *
 * @component
 */

import React, { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Alert, Button, Modal, message } from 'antd';
import axios from 'axios';
import { SubscriptionContext } from '../contexts/SubscriptionContext';

function UpgradeNotification() {
  const context = useContext(SubscriptionContext);
  const location = useLocation();
  const [capUpdateLoading, setCapUpdateLoading] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  let monthlyQuotaAlmostReached = false;
  let monthlyQuotaReached = false;
  let usageChargeQuotaAlmostReached = false;
  let usageChargeQuotaReached = false;

  const intervalTime = context.intervalTime;
  const usageQuota =
    intervalTime === 'EVERY_30_DAYS'
      ? context.monthlyQuota
      : context.monthlyQuota * 12;

  // Max number of extra returns the billing cap allows
  const maxExtraReturns =
    context.amountPerUsage > 0
      ? Math.floor(
          parseFloat(context.balanceRemaining) / context.amountPerUsage
        )
      : 0;

  // Per-return overage rate to display in the cap-update modal. Falls back to
  // the stored amountPerUsage if the backend didn't provide an expected value
  // (e.g. for legacy / unknown plans).
  const expectedAmountPerUsage =
    context.expectedAmountPerUsage ?? context.amountPerUsage;

  if (context.directBilling) {
    // TODO: remove this once all customers are on Shopify Billing
    return true;
  } else if (usageQuota === 0) {
    // this is for legacy plans with no monthly quota
    return true;
  } else if (context.usageChargeCount >= usageQuota) {
    const balanceRemaining = parseInt(context.balanceRemaining);
    const balanceUsed = context.balanceUsed;

    if (balanceRemaining === 0) {
      usageChargeQuotaReached = true;
    } else if (balanceUsed > balanceRemaining) {
      usageChargeQuotaAlmostReached = true;
    } else {
      monthlyQuotaReached = true;
    }
  } else {
    const percentOfQuota = (
      (context.usageChargeCount / usageQuota) *
      100
    ).toFixed(0);
    if (percentOfQuota > 80) {
      monthlyQuotaAlmostReached = true;
    }
  }

  // Max extra returns under the NEW cap (for the modal copy)
  const newCapMaxExtraReturns =
    context.expectedCappedAmount && expectedAmountPerUsage > 0
      ? Math.floor(context.expectedCappedAmount / expectedAmountPerUsage)
      : maxExtraReturns;

  //   //TODO: remove this test line
  // monthlyQuotaAlmostReached = true;

  const isOnAccountPage = location.pathname.startsWith('/dashboard/account');
  const isSubscriptionActive = context.planStatus === 'active';
  const showCapUpdateModal =
    context.needsCapUpdate &&
    isOnAccountPage &&
    isSubscriptionActive &&
    !isDismissed;

  const handleCapUpdate = async () => {
    setCapUpdateLoading(true);
    try {
      const { data } = await axios.post('/api/plans/update-cap');
      if (data.confirmationUrl) {
        window.location.href = data.confirmationUrl;
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        'Something went wrong. Please try again.';
      message.error(errorMessage);
      setCapUpdateLoading(false);
    }
  };

  return (
    <React.Fragment>
      {/* Cap update approval modal — shown once for all existing users on the account page */}
      <Modal
        open={showCapUpdateModal}
        title="Your extra return billing limit has been adjusted"
        closable={true}
        maskClosable={false}
        footer={[
          <Button key="later" onClick={() => setIsDismissed(true)}>
            Remind me later
          </Button>,
          <Button
            key="approve"
            type="primary"
            loading={capUpdateLoading}
            onClick={handleCapUpdate}
          >
            Approve on Shopify
          </Button>,
        ]}
        onCancel={() => setIsDismissed(true)}
      >
        <p>
          We've adjusted the extra return billing limit for your plan. This is
          not an upgrade or downgrade — your subscription plan remains
          unchanged.
        </p>
        <p>
          Your new billing limit is{' '}
          <strong>${context.expectedCappedAmount}</strong>, allowing up to{' '}
          <strong>{newCapMaxExtraReturns} extra returns</strong> per billing
          period. Extra returns are billed at{' '}
          <strong>${expectedAmountPerUsage}</strong> per return.
        </p>
        <p>
          Shopify requires a one-time approval to apply this adjustment to your
          account.
        </p>
      </Modal>

      {/*
        NOTE: All quota/usage banners are temporarily hidden. Equivalent
        notifications are already shown to merchants elsewhere (see the
        Plans/account page). Re-enable when needed.
      */}
      {/*
      <React.Fragment>
        {
          usageChargeQuotaAlmostReached
            ? (
              <Alert
                message={
                  <React.Fragment>
                    <span>
                    ⚠️ Your customers can start {(context.balanceRemaining / context.amountPerUsage).toFixed(0)} additional returns. After that customers will not be able to start a return until the end of the current billing period due to Shopify's usage-charge limit on your account. {" "}
                      <Button
                        size="small"
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://support.richreturns.io/en/articles/17693671-billing-for-extra-returns-shopify-usage-charge-limit-no-additional-returns-possible?utm_source=merchant_panel"
                      >
                    Read details
                      </Button>{" "}
                    Upgrade your plan now </span>
                    <Link to="/dashboard/account/profile">
                      <Button size="small" type="danger">See plans</Button>
                    </Link>
                  </React.Fragment>
                }
                banner={true}
                closable={true}
                type={"error"}
              />
            )
            : null
        }
      </React.Fragment>
      */}
      {/*
      <React.Fragment>
        {
          monthlyQuotaAlmostReached
            ? (
              <Alert
                message={
                  <React.Fragment>
                    <span>You have almost reached your monthly quota of {usageQuota} included returns. Upgrade your plan now to avoid interruptions for your customers. </span>
                    <Link to="/dashboard/account/profile">
                      <Button size="small" type="danger">See plans</Button>
                    </Link>
                  </React.Fragment>
                }
                banner={true}
                closable={true}
                type={"warning"}
              />
            )
            : null
        }
      </React.Fragment>

      <React.Fragment>
        {
          monthlyQuotaReached
            ? (
              <Alert
                message={
                  <React.Fragment>
                    <span>You have reached your quota of {usageQuota} included returns. Upgrade your plan now to avoid interruptions for your customers. </span>
                    <Link to="/dashboard/account/profile">
                      <Button size="small" type="danger">See plans</Button>
                    </Link>
                  </React.Fragment>
                }
                banner={true}
                closable={true}
                type={"error"}
              />
            )
            : null
        }
      </React.Fragment>

      <React.Fragment>
        {
          usageChargeQuotaReached
            ? (
              <Alert
                message={
                  <React.Fragment>
                    <span>
                    ⚠️⚠️⚠️ Your account has reached its limit of returns due to Shopify's usage-charge limit on your account {" "}
                      <Button
                        size="small"
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://support.richreturns.io/en/articles/17693671-billing-for-extra-returns-shopify-usage-charge-limit-no-additional-returns-possible?utm_source=merchant_panel"
                      >
                      Read details
                      </Button>.
                    Your customers are not able to start a new return until the end of the current billing period. Upgrade your plan now {" "}
                    </span>
                    <Link to="/dashboard/account">
                      <Button size="small" type="danger">See plans</Button>
                    </Link>
                  </React.Fragment>
                }
                banner={true}
                closable={true}
                type={"error"}
              />
            )
            : null
        }
      </React.Fragment>
      */}
    </React.Fragment>
  );
}

export default UpgradeNotification;
